import { useCallback, useRef, useState } from 'react';
import {
  createConversation as apiCreateConversation,
  updateConversation as apiUpdateConversation,
  getConversations,
} from '../api/messaging.api';

export function useConversations() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const hasLoadedRef = useRef(false);

  const loadConversations = useCallback(async (params = {}) => {
    // Only show loading state on initial load, not on subsequent refreshes
    if (!hasLoadedRef.current) {
      setLoading(true);
    }

    try {
      const data = await getConversations(params);
      setConversations(data?.conversations || []);
      hasLoadedRef.current = true;
    } catch (err) {
      console.error('[useConversations] loadConversations failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createConversation = useCallback(
    async (data) => {
      try {
        const result = await apiCreateConversation(data);
        const newConversation = result?.conversation ?? result;

        if (!newConversation?.id) {
          console.warn('[useConversations] createConversation: no id in response', result);
          return null;
        }

        // Add immediately so the UI responds without waiting for the server refetch
        setConversations((prev) => {
          if (prev.find((c) => `${c.id}` === `${newConversation.id}`)) return prev;
          return [newConversation, ...prev];
        });

        // Refetch in background to get full participant/user objects from server
        loadConversations();

        return newConversation;
      } catch (err) {
        console.error('[useConversations] createConversation failed:', err);
        return null;
      }
    },
    [loadConversations],
  );

  const updateConversation = useCallback(
    async (conversationId, data) => {
      try {
        const result = await apiUpdateConversation(conversationId, data);
        const updated = result?.conversation ?? result;
        if (updated) {
          setConversations((prev) =>
            prev.map((c) => (`${c.id}` === `${conversationId}` ? { ...c, ...updated } : c)),
          );
        }
        // Refetch in background to sync full server state
        loadConversations();
        return updated;
      } catch (err) {
        console.error('[useConversations] updateConversation failed:', err);
        return null;
      }
    },
    [loadConversations],
  );

  const injectConversation = useCallback((conversation) => {
    setConversations((prev) => {
      if (prev.find((c) => `${c.id}` === `${conversation.id}`)) return prev;
      return [conversation, ...prev];
    });
  }, []);

  return {
    conversations,
    loading,
    loadConversations,
    createConversation,
    updateConversation,
    injectConversation,
  };
}
